import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";

import { environment } from "src/environments/environment";
import { clearAuthStorage, parseStoredJson, readStoredValue, getGlobalUserInfo } from "../auth/auth-storage";

type AuthUser = {
  is_god_mode?: boolean | number | string;
  othernames?: string | null;
  surname?: string | null;
};

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private readonly acceptJsonHeaders = new HttpHeaders({ Accept: "application/json" });
  private readonly apiHost = environment.apiHost;
  private readonly godModeState = new BehaviorSubject<boolean>(this.isGodMode());
  readonly godMode$ = this.godModeState.asObservable();
  constructor(private http: HttpClient) {}

  triggerGodModeRefresh() {
    this.godModeState.next(this.isGodMode());
  }

  getApi(url: string) {
    return this.http.get(url, { headers: this.acceptJsonHeaders });
  }

  storemsg(url: string, payload: any) {
    return this.http.post(this.buildPublicUrl(url), payload);
  }

  get(url: string) {
    return this.http.get(this.buildTenantUrl(url));
  }

  getId(url: string, id: any) {
    return this.http.get(this.buildTenantUrl(this.joinPath(url, String(id))));
  }

  store(url: string, payload: any) {
    return this.http.post(this.buildTenantUrl(url), payload);
  }

  storePay(url: string, id: any, payload: any) {
    return this.http.post(this.buildTenantUrl(this.joinPath(url, String(id))), payload);
  }

  show(url: string, id: any) {
    return this.http.get(this.buildTenantUrl(this.joinPath(url, String(id))));
  }

  edit(url: string, id: any) {
    return this.http.get(this.buildTenantUrl(this.joinPath(url, String(id))));
  }

  update(url: string, id: any, payload: any) {
    return this.http.patch(this.buildTenantUrl(this.joinPath(url, String(id))), payload);
  }

  patch(url: string, payload: any) {
    return this.http.patch(this.buildTenantUrl(url), payload);
  }

  update2(url: string, id: any) {
    return this.http.patch(this.buildTenantUrl(this.joinPath(url, String(id))), {});
  }

  restore(url: string, id: any) {
    return this.http.patch(this.buildTenantUrl(this.joinPath(url, String(id))), {});
  }

  destroy(url: string, id: any) {
    return this.http.delete(this.buildTenantUrl(this.joinPath(url, String(id))));
  }

  createUser(url: string, payload: any) {
    return this.http.post(this.buildPublicUrl(url), payload);
  }

  authenticate(payload: any) {
    return this.http.post(this.buildPublicUrl("login"), payload);
  }

  domain(url: string, payload: any) {
    return this.http.post(this.buildPublicUrl(url), payload);
  }

  forgetPassword(url: string, sub: string, payload: any) {
    return this.http.post(this.buildTenantUrl(url, sub), payload);
  }

  user_array(str: string | null): string[] {
    if (!str || typeof str !== "string") {
      return ["Naira", "\u20A6", "NGN"];
    }

    const [name = "Naira", symbol = "\u20A6", code = "NGN"] = str
      .trim()
      .split(",")
      .map((value) => value.trim());

    return [name, symbol, code];
  }

  getCurrency(): string[] {
    return this.user_array(this.readStorage("currency"));
  }

  // PAYSTACK API CALLS
  // Proxied through the tenant API, which holds the Paystack secret key server-side.
  // The proxy returns Paystack's response body unchanged ({ status, message, data }).
  getBanks() {
    const params = new HttpParams().set("currency", this.getCurrency()[2]);

    return this.http.get(this.buildTenantUrl(environment.paystackProxy.banksPath), { params });
  }

  // CREATING SUB ACCOUNT
  createSubaccount(payload: any) {
    return this.http.post(this.buildTenantUrl(environment.paystackProxy.subaccountsPath), payload);
  }

  updateSubaccount(code: string, payload: any) {
    return this.http.put(
      this.buildTenantUrl(this.joinPath(environment.paystackProxy.subaccountsPath, encodeURIComponent(code))),
      payload
    );
  }

  // Get Subaccount Account Name from Paystack
  getName(accountNum: string, bankCode: string) {
    const params = new HttpParams()
      .set("account_number", accountNum)
      .set("bank_code", bankCode);

    return this.http.get(this.buildTenantUrl(environment.paystackProxy.resolveAccountPath), { params });
  }

  getICD(term: string) {
    const params = new HttpParams()
      .set("sf", "code,name")
      .set("maxList", "20")
      .set("terms", term)
      .set("_format", "json");

    return this.http.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
      headers: this.acceptJsonHeaders,
      params,
    });
  }

  getICDE(term: string) {
    const params = new HttpParams()
      .set("df", "code,title,type,name")
      .set("maxList", "20")
      .set("terms", term)
      .set("_format", "json");

    return this.http.get("https://clinicaltables.nlm.nih.gov/api/icd11_codes/v3/search", {
      headers: this.acceptJsonHeaders,
      params,
    });
  }

  getLoinc(term: string) {
    const params = new HttpParams()
      .set("df", "text,LOINC_NUM")
      .set("maxList", "20")
      .set("terms", term)
      .set("_format", "json");

    return this.http.get("https://clinicaltables.nlm.nih.gov/api/loinc_items/v3/search", {
      headers: this.acceptJsonHeaders,
      params,
    });
  }

  getDrug(value: string) {
    const params = new HttpParams().set("name", value);
    return this.http.get("https://rxnav.nlm.nih.gov/REST/drugs", {
      headers: this.acceptJsonHeaders,
      params,
    });
  }

  userID() {
    return this.readStorage("userID");
  }

  getToken() {
    return this.readStorage("token");
  }

  userData() {
    return this.readStorage("user") || this.readStorage("user_info");
  }

  currentUser(): any {
    return getGlobalUserInfo();
  }

  role() {
    return this.readStorage("role");
  }

  getAdminRole() {
    return this.readStorage("admin");
  }

  isGodMode(): boolean {
    const user = this.currentUser();
    const flag = user?.is_god_mode;

    return flag === 1 || flag === true || flag === "1" || flag === "true";
  }

  getUsername(): string {
    const explicitUsername = this.readStorage("username")?.trim();
    if (explicitUsername) {
      return explicitUsername;
    }

    const fullName = [this.readStorage("othernames"), this.readStorage("surname")]
      .filter((value): value is string => Boolean(value && value.trim()))
      .join(" ")
      .trim();

    return fullName || "User";
  }

  isLoggedIn() {
    const token = this.getToken();
    return Boolean(token && this.userData() && !this.hasExpiredJwt(token));
  }

  logout() {
    const formData = new FormData();
    formData.append("", "");

    return this.http.post(this.buildTenantUrl("logout"), formData);
  }

  clearStorage() {
    clearAuthStorage();
    this.godModeState.next(false);
  }

  resetPasswordFinal(host: string, payload: any): Observable<any> {
    const cleanedHost = host.replace(/\s+/g, "").toLowerCase();

    if (!this.isValidHost(cleanedHost)) {
      return throwError(new Error("Invalid password reset host"));
    }

    return this.http.post(`https://${cleanedHost}/api/password/reset`, payload);
  }

  private buildPublicUrl(path: string): string {
    return `https://${this.apiHost}${this.normalizePath(path)}`;
  }

  private buildTenantUrl(path: string, subdomainOverride?: string): string {
    const subdomain = this.sanitizeSubdomain(subdomainOverride ?? this.readStorage("subdomain"));
    const prefix = subdomain ? `${subdomain}.` : "";
    return `https://${prefix}${this.apiHost}${this.normalizePath(path)}`;
  }

  private normalizePath(path: string): string {
    return (path || "").replace(/^\/+/, "");
  }

  private joinPath(...parts: string[]): string {
    return parts
      .filter(Boolean)
      .map((part) => part.replace(/^\/+|\/+$/g, ""))
      .join("/");
  }

  private sanitizeSubdomain(subdomain: string | null | undefined): string {
    const value = (subdomain || "").trim().toLowerCase();
    return /^[a-z0-9-]+$/.test(value) ? value : "";
  }

  private readStorage(key: string): string | null {
    return readStoredValue(key);
  }

  private parseStoredJson<T>(key: string): T | null {
    return parseStoredJson<T>(key);
  }

  private hasExpiredJwt(token: string): boolean {
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(this.normalizeBase64(tokenParts[1])));
      return typeof payload.exp === "number" ? payload.exp * 1000 <= Date.now() : false;
    } catch {
      return false;
    }
  }

  private normalizeBase64(value: string): string {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const remainder = normalized.length % 4;

    if (remainder === 0) {
      return normalized;
    }

    return normalized + "=".repeat(4 - remainder);
  }

  private isValidHost(host: string): boolean {
    return /^(localhost|127\.0\.0\.1|(?:[a-z0-9-]+\.)+[a-z0-9-]+)$/i.test(host);
  }
}
